import * as React from 'react';
import { getDocument, GlobalWorkerOptions, TextLayer } from 'pdfjs-dist';

// =============================================================================
// pdf-document.tsx — Rendu pdf.js (canvas + couche texte) des octets PDF CGU (S7).
// =============================================================================
// Decision GATE_0 S7 : ce composant REMPLACE le corps placeholder du DocumentViewer par le
// rendu pdf.js des OCTETS REELS. Il est INTERNE au viewer : il ne connait rien du scroll-gate
// (qui reste pose en abstraction par DocumentViewer via useScrollToEnd, renderer-agnostique —
// discipline G4 : la detection « lu jusqu'au bout » N'EST PAS couplee au DOM pdf.js).
//
// Rendu : pour chaque page → un <canvas> (image) + une couche texte (TextLayer) absolument
// superposee (selection/lecteur d'ecran). Le conteneur reste un flux vertical scrollable normal,
// si bien que useScrollToEnd (sur scrollTop/scrollHeight du parent) fonctionne a l'identique.
//
// WORKER (Vite/ESM) : `GlobalWorkerOptions.workerSrc` pointe le worker pdf.js package via
// `new URL(..., import.meta.url)` — Vite le resout en asset builde (ESM, pas de config plugin
// speciale). Pose PARESSEUSEMENT au 1er rendu (idempotent).
//
// SEAM DE TEST (convention repo = injection de dependance, JAMAIS de module mock) : le moteur
// pdf.js est abstrait derriere `PdfEngine`. Le defaut (`defaultPdfEngine`) cable le vrai pdf.js ;
// les UT injectent un faux moteur via la prop `engine` — sans vi.mock (fragile sous plugin-react).
//
// a11y : la couche texte porte le contenu lisible (pdf.js la peuple) ; le canvas est decoratif
// (aria-hidden). La region scrollable focusable + le libelle d'etat restent geres par le parent.

/** Echelle de rendu fixe (lisibilite). La largeur reelle est contrainte par le conteneur CSS. */
const RENDER_SCALE = 1.2;

/** Viewport minimal consomme par le rendu (sous-ensemble de PageViewport pdf.js). */
export interface PdfViewport {
  readonly width: number;
  readonly height: number;
}

/** Page minimale consommee (sous-ensemble de PDFPageProxy pdf.js). */
export interface PdfPage {
  getViewport(opts: { scale: number }): PdfViewport;
  render(opts: {
    canvasContext: CanvasRenderingContext2D;
    viewport: PdfViewport;
  }): { promise: Promise<unknown> };
  streamTextContent(): unknown;
}

/** Document minimal consomme (sous-ensemble de PDFDocumentProxy pdf.js). */
export interface PdfDoc {
  readonly numPages: number;
  getPage(pageNumber: number): Promise<PdfPage>;
  destroy(): Promise<void>;
}

/**
 * Moteur pdf.js abstrait (seam d'injection). `load` parse les octets ; `renderTextLayer`
 * peuple la couche texte d'une page. Le defaut cable le vrai pdf.js ; les UT injectent un faux.
 */
export interface PdfEngine {
  load(data: ArrayBuffer): Promise<PdfDoc>;
  renderTextLayer(
    page: PdfPage,
    container: HTMLElement,
    viewport: PdfViewport,
  ): Promise<void>;
}

/** Pose le worker pdf.js UNE seule fois (paresseux). Vite resout l'URL en asset builde. */
let workerConfigured = false;
function ensureWorkerConfigured(): void {
  if (workerConfigured) {
    return;
  }
  GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
  ).href;
  workerConfigured = true;
}

/** Moteur de PRODUCTION : le vrai pdf.js (worker configure paresseusement). */
export const defaultPdfEngine: PdfEngine = {
  async load(data: ArrayBuffer): Promise<PdfDoc> {
    ensureWorkerConfigured();
    // Copie defensive : pdf.js prend possession (transfert) du buffer fourni.
    const loadingTask = getDocument({ data: data.slice(0) });
    return (await loadingTask.promise) as unknown as PdfDoc;
  },
  async renderTextLayer(
    page: PdfPage,
    container: HTMLElement,
    viewport: PdfViewport,
  ): Promise<void> {
    const textLayer = new TextLayer({
      textContentSource: page.streamTextContent() as never,
      container,
      viewport: viewport as never,
    });
    await textLayer.render();
  },
};

export interface PdfDocumentProps {
  /** Octets du PDF a rendre (du client legal : fixture locale en stub, GCS write-once en prod). */
  readonly data: ArrayBuffer;
  /** Libelle d'erreur deja localise (le rendu echoue rarement ; fail-soft visible). */
  readonly errorLabel?: string;
  /** Moteur pdf.js (defaut = vrai pdf.js). Injecte par les UT (seam de test). */
  readonly engine?: PdfEngine;
}

/** Etat du rendu (machine simple : chargement → pret | erreur). */
type RenderState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly pageCount: number }
  | { readonly status: 'error' };

/**
 * Rend l'integralite des pages du PDF (canvas + couche texte). Composant interne au
 * DocumentViewer : aucune notion de scroll-gate ici (discipline G4).
 */
export function PdfDocument({
  data,
  errorLabel = 'Le document n’a pas pu être affiché.',
  engine = defaultPdfEngine,
}: PdfDocumentProps): React.JSX.Element {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [state, setState] = React.useState<RenderState>({ status: 'loading' });

  React.useEffect(() => {
    let cancelled = false;
    let doc: PdfDoc | undefined;
    const container = containerRef.current;
    if (container === null) {
      return undefined;
    }
    // Repart d'un conteneur propre (re-render sur nouveaux octets).
    container.replaceChildren();
    setState({ status: 'loading' });

    async function renderAll(target: HTMLDivElement): Promise<void> {
      doc = await engine.load(data);
      if (cancelled) {
        return;
      }
      for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber += 1) {
        const page = await doc.getPage(pageNumber);
        if (cancelled) {
          return;
        }
        const viewport = page.getViewport({ scale: RENDER_SCALE });

        const pageEl = document.createElement('div');
        pageEl.className = 'relative mx-auto mb-4 last:mb-0 shadow-sm';
        pageEl.style.width = `${String(Math.floor(viewport.width))}px`;
        pageEl.style.height = `${String(Math.floor(viewport.height))}px`;
        // La couche texte pdf.js dimensionne ses spans via `calc(... * var(--scale-factor))`
        // (taille de police + geometrie de la couche). Sans cette variable, les calc() sont
        // invalides : les spans retombent sur une police par defaut et s'affichent en double
        // par-dessus le canvas (bug F14). On la cale sur l'echelle de rendu du viewport.
        pageEl.style.setProperty('--scale-factor', String(RENDER_SCALE));

        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.setAttribute('aria-hidden', 'true');
        const ctx = canvas.getContext('2d');
        if (ctx === null) {
          throw new Error('Contexte canvas 2D indisponible.');
        }
        pageEl.appendChild(canvas);

        const textLayerDiv = document.createElement('div');
        // Couche texte superposee (selection + lecteur d'ecran). Position calee sur le canvas.
        textLayerDiv.className = 'textLayer absolute inset-0';
        pageEl.appendChild(textLayerDiv);

        target.appendChild(pageEl);

        await page.render({ canvasContext: ctx, viewport }).promise;
        if (cancelled) {
          return;
        }
        await engine.renderTextLayer(page, textLayerDiv, viewport);
        if (cancelled) {
          return;
        }
      }
      if (!cancelled) {
        setState({ status: 'ready', pageCount: doc.numPages });
      }
    }

    renderAll(container).catch(() => {
      if (!cancelled) {
        setState({ status: 'error' });
      }
    });

    return (): void => {
      cancelled = true;
      // Libere les ressources pdf.js (worker/transport) si le document a ete charge.
      void doc?.destroy();
    };
  }, [data, engine]);

  return (
    <div>
      {state.status === 'loading' ? (
        <p role="status" className="text-sm text-muted-foreground">
          Chargement du document…
        </p>
      ) : null}
      {state.status === 'error' ? (
        <p role="alert" className="text-sm text-destructive">
          {errorLabel}
        </p>
      ) : null}
      {/* Conteneur des pages rendues (canvas + couche texte), peuple imperativement par pdf.js. */}
      <div ref={containerRef} />
    </div>
  );
}
